import { z } from 'zod'

import {
  serverItemSchema,
  nullableString,
  nullableNumber,
} from '../utils/schema'
import { ruleStates } from '../constants/misc'

type LoyaltyBonusRule = z.infer<typeof loyaltyBonusRuleSchema>
type LoyaltyBonusRuleInputs = z.infer<typeof loyaltyBonusRuleInputs>
type UpdateLoyaltyBonusRuleInputs = z.infer<typeof updateLoyaltyBonusRuleInputs>

const loyaltyBonusRuleSchema = serverItemSchema.merge(
  z.object({
    endDate: nullableString,
    expirationPointsDays: nullableNumber,
    isManual: z.boolean(),
    name: nullableString,
    startDate: nullableString,
    state: z.enum(ruleStates),
    text: nullableString,
    value: nullableNumber,
  })
)

const loyaltyBonusRuleBaseInputs = z.object({
  startDate: nullableString.transform((data) => data || null),
  state: z.enum(ruleStates),
  endDate: nullableString.transform((data) => data || null),
  expirationPointsDays: nullableString
    .transform((data) => (data ? parseFloat(data) : null))
    .or(
      z
        .number()
        .refine(
          (data) => data >= 0,
          'Please enter a number greater than or equal to 0.'
        )
    ),
})

const loyaltyBonusRuleInputs = z
  .union([
    z.object({
      type: z.literal('custom'),
      isManual: z.literal(true),
      text: nullableString.refine((val) => val == null || val.length, {
        message: 'Please enter a custom bonus.',
      }),
    }),
    z.object({
      type: z.literal('dollar'),
      isManual: z.literal(false),
      value: z
        .string()
        .min(1, { message: 'This field is required.' })
        .transform((data) => parseFloat(data))
        .or(z.number().positive('Please enter a number greater than 0.')),
    }),
  ])
  .and(loyaltyBonusRuleBaseInputs)

const updateLoyaltyBonusRuleInputs = serverItemSchema
  .merge(loyaltyBonusRuleBaseInputs)
  .merge(
    z.object({
      isManual: z.boolean(),
      name: nullableString,
      text: nullableString,
      value: nullableNumber,
    })
  )

const loyaltyBonusRewardSchema = loyaltyBonusRuleSchema.omit({
  expirationPointsDays: true,
  name: true,
  state: true,
})

export type {
  LoyaltyBonusRule,
  LoyaltyBonusRuleInputs,
  UpdateLoyaltyBonusRuleInputs,
}
export {
  loyaltyBonusRewardSchema,
  loyaltyBonusRuleInputs,
  loyaltyBonusRuleSchema,
  updateLoyaltyBonusRuleInputs,
}
